import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Community Fund (slice pot) data + voting.
 *
 * The fund is monthly and works-centered: viewers nominate published works,
 * then vote slices on the nominees. At month close the whole pot goes to the
 * winning work's total_slices_received. Campaign voting still exists
 * server-side, so the campaign summary/leaderboard is kept alongside.
 *
 * summary shape (from get_slice_pot_summary RPC):
 *   {
 *     pot_balance: number,
 *     period: { id, starts_at, ends_at } | null,
 *     leaderboard: [{ campaign_id, votes, voters, votes_last_48h }],
 *     my_votes_this_period: number
 *   }
 *
 * worksSummary shape (from get_pot_work_leaderboard RPC, anon-safe):
 *   {
 *     pot_balance: number,
 *     period: { id, starts_at, ends_at } | null,
 *     leaderboard: [{
 *       work_id, title, slug, poster_url,
 *       publisher: { id, company_name, brand_logo_url },
 *       votes, voters, nominations
 *     }]  // includes zero-vote nominees, votes DESC then nominations DESC
 *   }
 *
 * pastWinners: closed periods that awarded the pot to a campaign, newest
 * first, with the winning campaign (publisher + work) embedded.
 * pastWorkWinners: same for periods that awarded the pot to a work.
 */
export function useSlicePot() {
  const [summary, setSummary] = useState(null);
  const [worksSummary, setWorksSummary] = useState(null);
  const [pastWinners, setPastWinners] = useState([]);
  const [pastWorkWinners, setPastWorkWinners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const refetch = useCallback(async () => {
    try {
      const [summaryRes, worksRes, winnersRes, workWinnersRes] = await Promise.all([
        supabase.rpc('get_slice_pot_summary'),
        supabase.rpc('get_pot_work_leaderboard'),
        supabase
          .from('pot_voting_periods')
          .select(`
            id, starts_at, ends_at, pot_awarded, closed_at,
            winner:winning_campaign_id (
              id, title, campaign_image_urls, total_slices_raised, goal_slices, status,
              publisher:publisher_id ( id, company_name, brand_logo_url, is_verified ),
              work:work_id ( id, title, slug, poster_url, backdrop_url )
            )
          `)
          .eq('status', 'closed')
          .not('winning_campaign_id', 'is', null)
          .order('closed_at', { ascending: false })
          .limit(12),
        supabase
          .from('pot_voting_periods')
          .select(`
            id, starts_at, ends_at, pot_awarded, closed_at,
            winner:winning_work_id (
              id, title, slug, poster_url, backdrop_url,
              publisher:publisher_id ( id, company_name, brand_logo_url )
            )
          `)
          .eq('status', 'closed')
          .not('winning_work_id', 'is', null)
          .order('closed_at', { ascending: false })
          .limit(12),
      ]);
      if (summaryRes.error) throw summaryRes.error;
      setSummary(summaryRes.data ?? null);
      setWorksSummary(worksRes.error ? null : worksRes.data ?? null);
      setPastWinners(winnersRes.data ?? []);
      setPastWorkWinners(workWinnersRes.data ?? []);
    } catch (err) {
      console.error('Failed to fetch slice pot:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Stay in sync with wallet changes triggered elsewhere (donations, votes)
  useEffect(() => {
    window.addEventListener('sliceBalanceChanged', refetch);
    return () => window.removeEventListener('sliceBalanceChanged', refetch);
  }, [refetch]);

  /**
   * Contribute slices to the pot as votes for a campaign.
   * Resolves with { voted, new_balance, period_ends_at, pot_balance };
   * throws Error with a user-readable message on failure.
   */
  const vote = useCallback(async (campaignId, amount) => {
    const { data, error } = await supabase.rpc('vote_pot_slices', {
      p_campaign_id: campaignId,
      p_amount: amount,
    });
    if (error) throw error;
    if (!data?.success) throw new Error(data?.error || 'Vote failed');
    window.dispatchEvent(new Event('sliceBalanceChanged'));
    return data;
  }, []);

  /**
   * Contribute slices to the pot as votes for a nominated work.
   * Same result contract as vote().
   */
  const voteWork = useCallback(async (workId, amount) => {
    const { data, error } = await supabase.rpc('vote_pot_slices', {
      p_work_id: workId,
      p_amount: amount,
    });
    if (error) throw error;
    if (!data?.success) throw new Error(data?.error || 'Vote failed');
    window.dispatchEvent(new Event('sliceBalanceChanged'));
    return data;
  }, []);

  return {
    summary,
    worksSummary,
    pastWinners,
    pastWorkWinners,
    isLoading,
    refetch,
    vote,
    voteWork,
  };
}
